import React, {
  type FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import ModalMessage from "@components/AuthFlow/Helpers/ModalMessage";
import { GOOGLE_BUTTON } from "@components/GoogleIdentity/stateOptions";
import {
  trackError,
  trackStarted,
  trackSuccess,
  triggerTrackingClick,
  withPrefix,
} from "@tracking/tracking";
import { useTrackElementInjected } from "@tracking/useTrackElementInjected";
import { useGoogleSignInStatus } from "clientState/GoogleIdentityProvider";
import { useGoogleIdentity } from "clientState/useGoogleIdentity/useGoogleIdentity";
import { LOGIN_METHODS, useRememberMe } from "clientState/useRememberMe";
import { useTracking, useTranslation } from "@edreamsodigeo/frontend-core";
import { Box, Flex, Spinner } from "@edreamsodigeo/prisma-design-system";

import { type GoogleLoginProps } from "./contract";
import { getGoogleButtonId, renderGoogleButton } from "./googleButtonFunctions";
import TrackingGoogleButton, {
  getTrackingMutationErrorBtnId,
  getTrackingMutationSuccessBtnId,
  getTrackingUserSelectionBtnId,
} from "./TrackingGoogleButton";
import {
  getTrackingComponentInjectedBtnId,
  getTrackingGoogleDisplayBtnId,
} from "./TrackingGoogleButton/TrackingGoogleButton";

const ACTION_ERROR_ID = "_error:google";

const GoogleButton: FC<GoogleLoginProps> = ({
  screen,
  trackingPrefix,
  gaTrackingPrefix,
  closeScreen,
  name = GOOGLE_BUTTON,
}) => {
  const googleButtonId = getGoogleButtonId(name);
  const {
    isGoogleScriptLoaded,
    googleLoginSuccessToken,
    resetGoogleSignIn,
    state,
  } = useGoogleSignInStatus();
  const { mutate, loading } = useGoogleIdentity();
  const googleContainer = useRef<HTMLDivElement | null>(null);
  const trackEventTrackingPrefix = gaTrackingPrefix || trackingPrefix;
  const [, setRememberedUser] = useRememberMe();

  const { trackEvent } = useTracking();
  const { t } = useTranslation();
  useTrackElementInjected(
    googleButtonId,
    getTrackingComponentInjectedBtnId(name),
  );

  const trackOnClick = useCallback(() => {
    trackStarted({
      trackEvent,
      isGoogle: true,
      screen,
      prefix: trackEventTrackingPrefix,
    });
  }, [trackEvent, screen, trackEventTrackingPrefix]);

  const [hasMutationError, setHasMutationError] = useState(false);
  const renderGoogleButtonWhenItsReady = useCallback(() => {
    if (isGoogleScriptLoaded && googleContainer && googleContainer.current) {
      renderGoogleButton(
        trackOnClick,
        googleContainer.current.offsetWidth,
        name,
        googleButtonId,
      );
      triggerTrackingClick(getTrackingGoogleDisplayBtnId(name));
    }
  }, [
    isGoogleScriptLoaded,
    trackOnClick,
    googleContainer,
    name,
    googleButtonId,
  ]);

  const onError = useCallback(() => {
    trackError({
      trackEvent,
      isGoogle: true,
      screen,
      prefix: trackEventTrackingPrefix,
    });
    triggerTrackingClick(getTrackingMutationErrorBtnId(name));
    setHasMutationError(true);
    renderGoogleButtonWhenItsReady();
  }, [
    trackEvent,
    renderGoogleButtonWhenItsReady,
    screen,
    trackEventTrackingPrefix,
    name,
  ]);

  useEffect(() => {
    if (state === name && googleLoginSuccessToken !== null) {
      triggerTrackingClick(getTrackingUserSelectionBtnId(name));
      mutate(
        {
          request: {
            token: googleLoginSuccessToken,
          },
        },
        {
          onSuccess: ({ data }) => {
            if (data.googleLogin.success) {
              triggerTrackingClick(getTrackingMutationSuccessBtnId(name));
              trackSuccess({
                trackEvent,
                isGoogle: true,
                screen,
                prefix: trackEventTrackingPrefix,
              });
              setRememberedUser(LOGIN_METHODS.google, data?.googleLogin?.user);
              closeScreen?.();
            } else {
              onError();
            }
          },
          onError,
        },
      );
      resetGoogleSignIn();
    }
  }, [
    closeScreen,
    googleLoginSuccessToken,
    mutate,
    resetGoogleSignIn,
    screen,
    setRememberedUser,
    state,
    trackEvent,
    renderGoogleButtonWhenItsReady,
    trackEventTrackingPrefix,
    onError,
    name,
  ]);
  useEffect(() => {
    renderGoogleButtonWhenItsReady();
  }, [isGoogleScriptLoaded, googleContainer, renderGoogleButtonWhenItsReady]);

  return (
    <Box ref={googleContainer}>
      {hasMutationError && (
        <Box pb="1x_small">
          <ModalMessage
            type="ERROR"
            message={t("authentication.social.google.error")}
            trackingId={withPrefix(
              `${screen}${ACTION_ERROR_ID}`,
              trackingPrefix,
            )}
          />
        </Box>
      )}
      {!loading && (
        <Flex
          justifyContent="center"
          id={googleButtonId}
          data-testid={name}
          height="44px"
        />
      )}
      <TrackingGoogleButton
        screen={screen}
        trackingPrefix={trackingPrefix}
        name={name}
      />
      {loading && (
        <Flex justifyContent="center">
          <Spinner size="medium" />
        </Flex>
      )}
    </Box>
  );
};

export default GoogleButton;
